import { useState, useEffect } from 'react';
import LoadingAlt from '../global/loadingAlt';
import globalStyles from '../globals.module.css';
import componentStyles from './inDepthBetCard.module.css';
import eventBus from '../../service/eventBus';
import { 
    EVENT_TRIGGER_IN_DEPTH_BET_CARD,
    EVENT_UPDATE_IN_DEPTH_BET_ID,
    EVENT_UPDATE_ACTION_BAR_VIEW
} from '../../utils/constants';
import { FaBackspace } from 'react-icons/fa';
import SessionService from '../../service/sessionService';
import { getBetDetails } from '../../service/feedService';
import ScoutingReport from './scoutingReport';
import { getTeamStats, getMatchupInsights, getMatchupInjuries } from '../../service/statsService';
import AnalyticsService from '../../service/analyticsService';
import { EVENT_IN_DEPTH_VIEWED } from '../../utils/analyticsConstants';
import { isFeatureSupported } from '../../utils/featureUtil';

const InDepthBetCard = () => {
    const componentId = 'InDepthBetCard';
    const [betCardData, setBetCardData] = useState(null);
    const [inDepthBetCardTeamStats, setInDepthBetCardTeamStats] = useState(null);
    const [inDepthBetCardMatchupInsights, setInDepthBetCardMatchupInsights] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [betId, setBetId] = useState(null);
    const [matchupInjuries, setMatchupInjuries] = useState({});

    const handleClose = () => {
        console.log(`Event ${EVENT_TRIGGER_IN_DEPTH_BET_CARD} triggered with value`, false);
        eventBus.emit(EVENT_TRIGGER_IN_DEPTH_BET_CARD, false);
        setBetCardData(null);
        
        const prevView = SessionService.getInstance().getPrevView();
        if (prevView) {
            eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, prevView);
            SessionService.getInstance().setPrevView(null);
        }
        else {
            eventBus.emit(EVENT_UPDATE_ACTION_BAR_VIEW, 'props');
        }
    };

    const fetchTeamStats = async () => {
        const sportId = betCardData && betCardData.sportType || null;
        if (!sportId || !isFeatureSupported('gameLogs', sportId)) {
            return;
        }

        const homeTeamId = betCardData && betCardData.homeTeamStatspyderId || null;
        const awayTeamId = betCardData && betCardData.awayTeamStatspyderId || null;
        
        if (sportId && homeTeamId && awayTeamId) {
            const args = [
                {
                    teamName: betCardData.homeTeam,
                    teamId: homeTeamId,
                    sportId: sportId
                },
                {
                    teamName: betCardData.awayTeam,
                    teamId: awayTeamId,
                    sportId: sportId
                }
            ];

            Promise.all(
                args.map(arg =>
                    getTeamStats(arg['teamId'], arg['sportId'])
                        .then(response => ({ teamName: arg.teamName, data: response.data }))
                        .catch(error => ({ teamName: arg.teamName, error }))
                )
            ).then(results => {
                let teamStats = { ...inDepthBetCardTeamStats };
                results.forEach(result => {
                    if (result.data && result.data.success) {
                        teamStats[result.teamName] = result.data.results && result.data.results.length > 0 ? result.data.results : {};
                    } else if (result.error) {
                        console.error(`Error fetching team stats for ${result.teamName}:`, result.error);
                    }
                });
                setInDepthBetCardTeamStats(teamStats);
                console.log('Team Stats:', teamStats);
            })
            .catch(error => {
                console.error('Error fetching team stats:', error);
            });
        }
    }

    const fetchMatchupInsights = async () => {
        const sportId = betCardData && betCardData.sportType || null;
        if (!sportId || !isFeatureSupported('historicalMatchup', sportId)) {
            return;
        }

        const homeTeamId = betCardData && betCardData.homeTeamStatspyderId || null;
        const awayTeamId = betCardData && betCardData.awayTeamStatspyderId || null;
        
        if (sportId && homeTeamId && awayTeamId) {
            const request = {
                'sport': sportId,
                'teamId': homeTeamId,
                'opponentId': awayTeamId
            };

            getMatchupInsights(request).then(response => {
                console.log('Matchup Insights:', response);
                if (response.data && response.data.success) {
                    setInDepthBetCardMatchupInsights(response.data.results);
                }
            }).catch(error => {
                console.error('Error fetching matchup insights:', error);
            });
        }
    }

    const fetchMatchupInjuries = async () => {
        const sportId = betCardData && betCardData.sportType || null;
        if (!sportId || !isFeatureSupported('injuries', sportId)) {
            return;
        }

        const homeTeamId = betCardData && betCardData.homeTeamStatspyderId || null;
        const awayTeamId = betCardData && betCardData.awayTeamStatspyderId || null;
        const homeTeamName = betCardData && betCardData.homeTeam || null;
        const awayTeamName = betCardData && betCardData.awayTeam || null;
        const teams = [{team: homeTeamName, teamId: homeTeamId}, {team: awayTeamName, teamId: awayTeamId}]
        let requestMatchupInjuries = {};
        // Fetch matchup injuries for both teams in parallel and set state after all are complete
        const injuryRequests = teams.map(team => {
            const request = {
                'sport': sportId,
                'teamId': team.teamId
            };
            // Attach team name to each promise for mapping results
            return getMatchupInjuries(request)
                .then(response => ({
                    team: team.team,
                    data: response.data
                }))
                .catch(error => ({
                    team: team.team,
                    error
                }));
        });

        Promise.all(injuryRequests)
            .then(results => {
                let matchupInjuries = {};
                let ignoreSettingInjuries = false;
                results.forEach(result => {
                    if (result.data && result.data.success) {
                        matchupInjuries[result.team] = result.data.results;
                    } else if (result.error) {
                        console.error(`Error fetching matchup injuries `, result.error);
                        ignoreSettingInjuries = true;
                    } else {
                        ignoreSettingInjuries = true;
                    }
                });
                if (!ignoreSettingInjuries) {
                    setMatchupInjuries(matchupInjuries);
                    console.log('Matchup Injuries:', matchupInjuries);
                }
                
            })
            .catch(error => {
                console.error('Error fetching matchup injuries:', error);
            });
    }

    useEffect(() => {
        eventBus.on(componentId, EVENT_UPDATE_IN_DEPTH_BET_ID, (betId) => {
            console.log(`Event ${EVENT_UPDATE_IN_DEPTH_BET_ID} received with value`, betId);
            setBetId(betId);
            const request = { 'betId': betId };
            getBetDetails(request).then(response => {
                const { data } = response;
                if (data.success) {
                    const results = data.results;
                    setBetCardData(results);
                    AnalyticsService.getInstance().track(EVENT_IN_DEPTH_VIEWED, {
                        betId: String(betId ?? ''),
                        scoutingReportId: results?.scoutingReportId ?? results?.id ?? ''
                    });
                    setTimeout(fetchTeamStats, 3000);
                    setTimeout(fetchMatchupInsights, 3000);
                    setTimeout(fetchMatchupInjuries, 3000);
                }
                else {
                    console.error('Error fetching indepth bet:', data.error);
                }
                setIsLoading(false);
            }).catch(error => {
                console.error('Error fetching indepth bet:', error);
            });
        });

        return () => {
            eventBus.off(componentId, EVENT_UPDATE_IN_DEPTH_BET_ID);
        };
    }, []);

    useEffect(() => {
        if (betCardData) {
            fetchTeamStats();
            fetchMatchupInsights();
            fetchMatchupInjuries();
        }
    }, [betCardData]);

    if (betCardData === null) {
        return (
            <div className={componentStyles.betDataCardContainer}>
                <LoadingAlt />
            </div>
        )
    } else {
        return (
            <div className={componentStyles.betDataCardContainer}>
                <div className={componentStyles.betDataCardHeader}>
                    <div className={componentStyles.betDataCardHeaderClose}>
                        <button type="button" aria-label="Close analysis" className={`${globalStyles.iconButton} ${globalStyles.squareButton} ${globalStyles.compactButton}`} onClick={handleClose}>
                            <FaBackspace />
                        </button>
                    </div>
                </div>
                <div className={componentStyles.betDataCardBody}>
                    <ScoutingReport 
                        betCardData={betCardData} 
                        inDepthBetCardTeamStats={inDepthBetCardTeamStats} 
                        inDepthBetCardMatchupInsights={inDepthBetCardMatchupInsights} 
                        inDepthBetCardMatchupInjuries={matchupInjuries}
                    />
                </div>
            </div>                
        )
    }
}

export default InDepthBetCard;
